const amountFormat = (fractionDigits: number) =>
  new Intl.NumberFormat('es-ES', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
    // es-ES skips the separator on 4-digit numbers by default; the design shows "1.800".
    useGrouping: 'always'
  } as unknown as Intl.NumberFormatOptions);

const WHOLE_FORMAT = amountFormat(0);
const CENTS_FORMAT = amountFormat(2);

const MADRID_TZ = 'Europe/Madrid';

/**
 * `1800` → `1.800`, `3850.5` → `3.850,50`: no decimals for a whole amount, otherwise always two (qa-diseno-final
 * §1.5, ENF-2468 D-11). Rounded to the cent first, so `99.999` is `100`, not `100,00`.
 */
export function formatAmount(value: number): string {
  const cents = Math.round(value * 100);
  return cents % 100 === 0 ? WHOLE_FORMAT.format(cents / 100) : CENTS_FORMAT.format(cents / 100);
}

/** `1800` → `1.800 €`. */
export function formatEuros(value: number): string {
  return `${formatAmount(value)} €`;
}

/** `12 oct 2026` (Europe/Madrid, as the admin sees the day). */
export function formatLongDate(iso: string): string {
  return (
    new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: 'short', year: 'numeric', timeZone: MADRID_TZ })
      .format(new Date(iso))
      .replace(/\./g, '')
      // ICU abbreviates September as "sept" while every other month has three letters.
      .replace(/\bsept\b/, 'sep')
  );
}

/** `12/10/26`. */
export function formatShortDate(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit', timeZone: MADRID_TZ }).format(new Date(iso));
}

/** `Octubre`. */
export function formatMonthName(iso: string): string {
  const month = new Intl.DateTimeFormat('es-ES', { month: 'long', timeZone: MADRID_TZ }).format(new Date(iso));
  return month.charAt(0).toUpperCase() + month.slice(1);
}

/** Local `Date` → `YYYY-MM-DD` of the calendar day the user picked. */
export function toDayString(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** `YYYY-MM-DD` → local `Date` at midnight (no timezone shift). */
export function fromDayString(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/**
 * Installments cell: "2 / 6" for installments, "1" for a single payment. A purchase emptied by an unlink (D35) has no
 * charges and keeps its plan, so it shows "0 / N", also a single payment ("0 / 1"), never "1".
 */
export function formatInstallments(paid: number, total: number): string {
  return total > 1 || paid === 0 ? `${paid} / ${Math.max(total, 1)}` : '1';
}

/** Amount with its billing cycle: `9 €/mes`, `31 €/año`, `20 €/3 meses`. Without a cycle, just the amount. */
export function formatPeriodicAmount(
  amount: number,
  frequency: number | null,
  unitOne: string | null,
  unitMany: string | null,
  amountText: string = formatEuros(amount)
): string {
  if (!unitOne) return amountText;
  const every = frequency && frequency > 1 && unitMany ? `${frequency} ${unitMany}` : unitOne;
  return `${amountText}/${every}`;
}

/** `+34 612458903`; empty when the student has no phone. */
export function formatPhone(phone: string | null, phonePrefix: string | null): string {
  if (!phone) return '';
  const prefix = phonePrefix ? (phonePrefix.startsWith('+') ? phonePrefix : `+${phonePrefix}`) : '';
  return `${prefix} ${groupPhoneDigits(phone)}`.trim();
}

/**
 * Display only (Figma «+34 612 458 903»): a number of digits is split in blocks of 3, the last one taking a lone
 * remaining digit («310 123 4511»). Anything that is not plain digits (dashes, letters, a prefix typed in) is shown as
 * stored. The search keeps matching the stored value.
 */
function groupPhoneDigits(phone: string): string {
  const digits = phone.replace(/\s+/g, '');
  if (!/^\d+$/.test(digits) || digits.length < 6) return phone.trim();
  const groups = digits.match(/\d{1,3}/g)!;
  if (groups.length > 1 && groups[groups.length - 1].length === 1) {
    groups[groups.length - 2] += groups.pop();
  }
  return groups.join(' ');
}

/** Key of a counter text in singular when there is exactly one item: `X` → `X_ONE` («1–1 de 1 pedido»). */
export function countKey(key: string, total: number): string {
  return total === 1 ? `${key}_ONE` : key;
}
